import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, Plus, X, MessageCircle, ArrowDownLeft, ArrowUpRight, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { buildPaymentRequestMessage, openWhatsApp } from "@/lib/whatsapp";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es", { dateStyle: "medium" }); } catch { return iso; } };
const openMyReceipt = async (pid) => {
  const w = window.open("", "_blank");
  try {
    const r = await adminApi.get(`/portal/payments/${pid}/receipt`);
    const data = r.data.receipt;
    if (!data) { if (w) w.close(); toast.error("Sin comprobante"); return; }
    const isData = typeof data === "string" && data.startsWith("data:");
    const url = isData ? URL.createObjectURL(await (await fetch(data)).blob()) : data;
    if (w) w.location.href = url; else window.open(url, "_blank");
    if (isData) setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { if (w) w.close(); toast.error("Sin comprobante"); }
};

export const PAY_METHODS = [
  ["EFECTIVO", "Efectivo"],
  ["ZELLE", "Zelle"],
  ["WIRE", "Wire / Transferencia"],
  ["USDT", "USDT"],
  ["OTRO", "Otro"],
];
const METHOD_LABEL = Object.fromEntries(PAY_METHODS);
const PST = {
  PENDIENTE: ["Pendiente", "bg-amber-50 text-amber-700 border-amber-200"],
  CONFIRMADO: ["Confirmado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  CANCELADO: ["Cancelado", "bg-zinc-100 text-zinc-500 border-zinc-200"],
};

// Campos dinámicos por método (en el Portal)
export function MethodFields({ method, details, setD }) {
  const f = (k, label, ph, req) => (
    <label className="flex flex-col gap-0.5">
      <span className="text-[11px] text-zinc-500 px-1">{label}{req ? " *" : ""}</span>
      <Input data-testid={`pay-field-${k}`} value={details[k] || ""} onChange={(e) => setD(k, e.target.value)} placeholder={ph} className="h-10 rounded-lg" />
    </label>
  );
  if (method === "EFECTIVO") return <div className="grid gap-2">{f("pickup_name", "Nombre de quien retirará", "Nombre completo", true)}{f("pickup_phone", "Teléfono", "Opcional")}{f("pickup_document", "Documento / Cédula", "Opcional")}</div>;
  if (method === "ZELLE") return <div className="grid gap-2">{f("recipient", "Destinatario", "Opcional")}{f("zelle_contact", "Email / Teléfono Zelle", "Opcional")}</div>;
  if (method === "WIRE") return <div className="grid gap-2">{f("beneficiary", "Beneficiario", "Opcional")}{f("bank", "Banco", "Opcional")}{f("reference", "Datos / Referencia", "Opcional")}</div>;
  if (method === "USDT") return <div className="grid gap-2">{f("network", "Red (TRC20 / ERC20...)", "Opcional")}{f("wallet", "Wallet / Dirección", "Opcional")}</div>;
  return <div className="grid gap-2">{f("method_desc", "Método", "Describe el método")}{f("instructions", "Instrucciones", "Opcional")}</div>;
}

function RequestDialog({ account, onClose, onCreated }) {
  const [method, setMethod] = useState("EFECTIVO");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const setD = (k, v) => setDetails((p) => ({ ...p, [k]: v }));
  const amt = Number(amount) || 0;
  const disp = account?.disponible ?? 0;
  const after = Math.round((disp - amt) * 100) / 100;
  const invalid = amt <= 0 || amt > disp + 1e-6 || (method === "EFECTIVO" && !(details.pickup_name || "").trim());
  const submit = async () => {
    setSaving(true);
    try {
      const r = await adminApi.post("/portal/payments", { method, amount: amt, note: note || null, details });
      toast.success("Solicitud enviada");
      onCreated(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="pay-request-dialog">
        <DialogHeader><DialogTitle>Solicitar pago / retiro</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-[11px] text-zinc-500 px-1 mb-1">¿Cómo quieres recibir el dinero?</p>
            <div className="grid grid-cols-3 gap-1.5">
              {PAY_METHODS.map(([k, l]) => (
                <button key={k} data-testid={`pay-method-${k}`} onClick={() => setMethod(k)}
                  className={`h-9 rounded-lg text-xs font-medium border transition-colors ${method === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>{l}</button>
              ))}
            </div>
          </div>
          <label className="flex flex-col gap-0.5">
            <span className="text-[11px] text-zinc-500 px-1">Monto *</span>
            <Input data-testid="pay-amount" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 rounded-xl text-lg font-semibold" placeholder="0" />
          </label>
          <MethodFields method={method} details={details} setD={setD} />
          <label className="flex flex-col gap-0.5">
            <span className="text-[11px] text-zinc-500 px-1">Nota (opcional)</span>
            <Input data-testid="pay-note" value={note} onChange={(e) => setNote(e.target.value)} className="h-10 rounded-lg" />
          </label>
          <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs space-y-1" data-testid="pay-preview">
            <div className="flex justify-between"><span className="text-zinc-500">Saldo acumulado</span><span className="font-medium">{money(account?.saldo_total)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Ya comprometido</span><span className="font-medium">{money(account?.pending_payments)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Disponible</span><span className="font-medium">{money(disp)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Monto solicitado</span><span className="font-medium">{money(amt)}</span></div>
            <div className="flex justify-between border-t border-zinc-200 pt-1 mt-1"><span className="text-zinc-500">Disponible después</span><span className={`font-semibold ${after < 0 ? "text-rose-600" : "text-zinc-900"}`}>{money(after)}</span></div>
          </div>
          {amt > disp + 1e-6 && <p className="text-xs text-rose-600" data-testid="pay-over">El monto excede el disponible.</p>}
          <Button data-testid="pay-submit" onClick={submit} disabled={saving || invalid} className="h-11 w-full rounded-xl bg-zinc-900 hover:bg-zinc-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar solicitud"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MiDinero({ account, onChanged, initialStatus = null }) {
  const [payments, setPayments] = useState([]);
  const [movs, setMovs] = useState([]);
  const [showReq, setShowReq] = useState(false);
  const [sub, setSub] = useState("resumen");
  const [statusFilter, setStatusFilter] = useState(initialStatus || "TODAS");
  const load = () => {
    adminApi.get("/portal/payments").then((r) => setPayments(r.data)).catch(() => setPayments([]));
    adminApi.get("/portal/account/movements").then((r) => setMovs(r.data)).catch(() => setMovs([]));
  };
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChanged?.(); };
  const cancel = async (id) => {
    try { await adminApi.post(`/portal/payments/${id}/cancel`); toast.success("Solicitud cancelada"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const pendingPayments = useMemo(() => payments.filter((p) => p.status === "PENDIENTE"), [payments]);
  const PAYFILTERS = [["TODAS", "Todas"], ["PENDIENTE", "Pendientes"], ["CONFIRMADO", "Confirmadas"], ["CANCELADO", "Canceladas"]];
  const visiblePayments = useMemo(() => (statusFilter === "TODAS" ? payments : payments.filter((p) => p.status === statusFilter)), [payments, statusFilter]);

  return (
    <div className="space-y-5" data-testid="portal-dinero">
      {/* Hero saldo */}
      <div className="rounded-3xl bg-zinc-900 text-white p-5" data-testid="dinero-hero">
        <p className="text-[12px] uppercase tracking-wider text-white/50 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Mi dinero en PuntoCell</p>
        <p className="text-4xl font-bold mt-2" data-testid="dinero-saldo">{money(account?.saldo_total)}</p>
        <p className="text-[11px] text-white/40 mt-0.5">Saldo acumulado</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-2xl bg-white/10 p-3"><p className="text-[11px] text-white/50">Pagos/retiros pendientes</p><p className="text-lg font-semibold mt-0.5 text-amber-300" data-testid="dinero-pendiente">{money(account?.pending_payments)}</p></div>
          <div className="rounded-2xl bg-white/10 p-3"><p className="text-[11px] text-white/50">Disponible</p><p className="text-lg font-semibold mt-0.5 text-emerald-300" data-testid="dinero-disponible">{money(account?.disponible)}</p></div>
        </div>
        {account?.pending_sales_count > 0 && <p className="text-[12px] text-white/60 mt-3" data-testid="dinero-pending-sales">⏳ Ventas con monto pendiente: {account.pending_sales_count}</p>}
        <Button data-testid="dinero-request-btn" onClick={() => setShowReq(true)} className="h-11 w-full rounded-xl bg-white text-zinc-900 hover:bg-zinc-100 mt-4 font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Solicitar pago / retiro</Button>
      </div>

      <div className="flex gap-2">
        {[["resumen", "Solicitudes"], ["movimientos", "Movimientos"]].map(([k, l]) => (
          <button key={k} data-testid={`dinero-sub-${k}`} onClick={() => setSub(k)}
            className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${sub === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{l}</button>
        ))}
      </div>

      {sub === "resumen" ? (
        <>
          <div className="flex flex-wrap gap-2" data-testid="dinero-pay-filters">
            {PAYFILTERS.map(([k, l]) => (
              <button key={k} data-testid={`dinero-pay-filter-${k}`} onClick={() => setStatusFilter(k)}
                className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${statusFilter === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                {l} <span className={statusFilter === k ? "text-white/60" : "text-zinc-400"}>({k === "TODAS" ? payments.length : payments.filter((p) => p.status === k).length})</span>
              </button>
            ))}
          </div>
          {visiblePayments.length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center" data-testid="dinero-no-req">{statusFilter === "TODAS" ? "Aún no has solicitado pagos." : "Sin solicitudes en esta categoría."}</p> : (
          <div className="space-y-2" data-testid="dinero-requests">
            {visiblePayments.map((p) => {
              const [lbl, cls] = PST[p.status] || ["", ""];
              return (
                <div key={p.id} data-testid={`pay-row-${p.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-zinc-900">{money(p.amount)} <span className="font-normal text-zinc-400 text-sm">· {METHOD_LABEL[p.method] || p.method}</span></p>
                      <p className="text-xs text-zinc-400 mt-0.5">{p.id} · {fmtDate(p.created_at)}</p>
                      {p.note && <p className="text-xs text-zinc-500 mt-0.5">{p.note}</p>}
                    </div>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{lbl}</span>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" data-testid={`pay-share-${p.id}`} onClick={() => openWhatsApp(buildPaymentRequestMessage(p))} className="h-8 rounded-lg text-xs bg-[#25D366] hover:bg-[#1ebe5b] text-white"><MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp</Button>
                    {p.has_receipt && <Button variant="outline" size="sm" data-testid={`pay-view-receipt-${p.id}`} onClick={() => openMyReceipt(p.id)} className="h-8 rounded-lg text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"><FileText className="w-3.5 h-3.5 mr-1" /> Ver comprobante</Button>}
                    {p.status === "PENDIENTE" && <Button variant="outline" size="sm" data-testid={`pay-cancel-${p.id}`} onClick={() => cancel(p.id)} className="h-8 rounded-lg text-xs text-rose-600 border-rose-200 hover:bg-rose-50"><X className="w-3.5 h-3.5 mr-1" /> Cancelar</Button>}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </>
      ) : (
        movs.length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center" data-testid="dinero-no-mov">Sin movimientos todavía.</p> : (
          <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100" data-testid="dinero-movs">
            {movs.map((m, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`mov-${m.reference || i}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`grid place-items-center w-8 h-8 rounded-full shrink-0 ${m.sign === "+" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>{m.sign === "+" ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}</span>
                  <div className="min-w-0"><p className="text-sm font-medium text-zinc-800 truncate">{m.label}</p><p className="text-[11px] text-zinc-400">{m.reference || "—"} · {fmtDate(m.date)}</p></div>
                </div>
                <span className={`text-sm font-semibold shrink-0 ${m.sign === "+" ? "text-emerald-600" : "text-rose-600"}`}>{m.sign}{money(m.amount)}</span>
              </div>
            ))}
          </div>
        )
      )}

      {pendingPayments.length > 0 && sub === "resumen" && (
        <p className="text-[11px] text-zinc-400 flex items-center gap-1" data-testid="dinero-reserved-note"><Clock className="w-3 h-3" /> Las solicitudes pendientes reservan tu disponible hasta que PuntoCell confirme el pago.</p>
      )}

      {showReq && <RequestDialog account={account} onClose={() => setShowReq(false)} onCreated={refresh} />}
    </div>
  );
}
